import {
  appendChange,
  applyBrand,
  applyOps,
  type BrandTokens,
  boardRects,
  diff,
  type LiveOp,
  normalizeVideoLayers,
  pageDocument,
  pruneBindings,
  type SceneDocument,
  supportsBoard,
} from "@cva/shared";
import { type Draft, produce } from "immer";
import { createContext, useContext } from "react";
import { createStore, type StoreApi, useStore } from "zustand";

/**
 * Editor state. The document is immutable (Immer). Undo/redo keeps the
 * operations of each step and their inverse (docs/17-live-kollaboration.md):
 * undoing restores only the values the step changed, so it never takes back
 * what others changed meanwhile.
 */

/** One undo step: operations forward and back. */
export interface HistoryEntry {
  redo: LiveOp[];
  undo: LiveOp[];
}

/** What caused the last document change (the live sync sends only edits). */
export type ChangeKind = "edit" | "local" | "remote";

export interface SnapGuide {
  axis: "x" | "y";
  /** Position of the line in page coordinates. */
  position: number;
  from: number;
  to: number;
}

export interface EditorState {
  doc: SceneDocument;
  pageIndex: number;
  selection: string[];
  hoverId: string | null;
  editingTextId: string | null;
  croppingId: string | null;
  /** Screen pixels per document pixel. */
  zoom: number;
  pan: { x: number; y: number };
  readOnly: boolean;
  guides: SnapGuide[];
  past: HistoryEntry[];
  future: HistoryEntry[];
  /** Merge key of the last history entry (typing in a field, nudging). */
  lastMergeKey: string | null;
  lastMergeAt: number;
  /** Document when the running gesture started (drags compute from it). */
  gestureBase: SceneDocument | null;
  /** Changes of the running gesture (one undo step). */
  gestureEntry: HistoryEntry | null;
  /**
   * Changes of others may show during the gesture. Pointer drags wait until the
   * pointer is up (some recompute whole lists from `gestureBase`); long-lived
   * gestures (typing, colour pickers) take them in right away.
   */
  gestureLive: boolean;
  changeKind: ChangeKind;
  /**
   * Tokens of the brand kit the document's project uses (null while loading or
   * when the organization has no kit).
   */
  brand: BrandTokens | null;
  /**
   * Asks the canvas to move the view: all pages ("all"), the active page ("page") or
   * just bring the active page into view ("focus"). The canvas knows the viewport.
   */
  fitRequest: { mode: "all" | "page" | "focus"; id: number } | null;
}

export interface EditorActions {
  /** Changes the document and records an undo step. */
  update(recipe: (draft: Draft<SceneDocument>) => void, options?: { merge?: string }): void;
  /** Changes the document without an undo step (measurements, live previews). */
  patch(recipe: (draft: Draft<SceneDocument>) => void): void;
  beginGesture(options?: { live?: boolean }): void;
  endGesture(): void;
  cancelGesture(): void;
  undo(): void;
  redo(): void;
  replaceDocument(doc: SceneDocument, options?: { keepHistory?: boolean }): void;
  /** The state after changes of others (not an undo step, not sent again). */
  applyRemote(doc: SceneDocument): void;
  select(ids: string[], mode?: "replace" | "toggle" | "add"): void;
  setHover(id: string | null): void;
  setPage(index: number): void;
  /** Opens a page and, on a board, brings it into view. */
  goToPage(index: number): void;
  setView(view: { zoom?: number; pan?: { x: number; y: number } }): void;
  setEditingText(id: string | null): void;
  setCropping(id: string | null): void;
  setGuides(guides: SnapGuide[]): void;
  setReadOnly(readOnly: boolean): void;
  /**
   * Sets the brand kit and refreshes all linked values. Not an undo step: the
   * history is refreshed too, so undo never brings back outdated brand values.
   * Null (no kit): linked values keep their last resolved values.
   */
  setBrand(tokens: BrandTokens | null): void;
  requestFit(mode: "all" | "page" | "focus"): void;
}

export type EditorStore = StoreApi<EditorState & EditorActions>;

const HISTORY_LIMIT = 200;
const MERGE_WINDOW_MS = 1200;

function withoutMissing(doc: SceneDocument, ids: string[]): string[] {
  return ids.filter((id) => doc.elements[id]);
}

const EMPTY_ENTRY: HistoryEntry = { redo: [], undo: [] };

/**
 * Videos: every element on a track, the tracks consistent (video/tracks.ts).
 * During a drag, emptied tracks stay (the rows would jump under the pointer).
 */
function normalized(doc: SceneDocument, compact: boolean): SceneDocument {
  if (doc.kind !== "video") return doc;
  return produce(doc, (draft) => normalizeVideoLayers(draft as SceneDocument, { compact }));
}

export function createEditorStore(doc: SceneDocument, readOnly: boolean): EditorStore {
  return createStore<EditorState & EditorActions>()((set, get) => {
    /** Applies history operations; brand values are resolved again afterwards. */
    const applyHistory = (current: SceneDocument, ops: LiveOp[]) => {
      const next = applyOps(current, ops);
      const brand = get().brand;
      return brand && next !== current ? applyBrand(next, brand) : next;
    };

    /** Undo/redo: applies `entry.undo` of the stacks' top, skipping steps that change nothing. */
    const travel = (from: "past" | "future") => {
      const state = get();
      if (state.readOnly || state.gestureBase) return;
      const stack = [...state[from]];
      while (stack.length > 0) {
        const entry = from === "past" ? stack.pop()! : stack.shift()!;
        const next = applyHistory(state.doc, from === "past" ? entry.undo : entry.redo);
        // The step on the other stack changes exactly what this one changed.
        const forward = diff(state.doc, next);
        if (forward.length === 0) continue;
        const backward = diff(next, state.doc);
        set({
          doc: next,
          [from]: stack,
          ...(from === "past"
            ? {
                future: [{ redo: backward, undo: forward }, ...state.future].slice(
                  0,
                  HISTORY_LIMIT,
                ),
              }
            : { past: [...state.past, { redo: forward, undo: backward }].slice(-HISTORY_LIMIT) }),
          selection: withoutMissing(next, state.selection),
          pageIndex: Math.min(state.pageIndex, next.pages.length - 1),
          lastMergeKey: null,
          editingTextId: null,
          croppingId: null,
          changeKind: "edit",
        } as Partial<EditorState>);
        return;
      }
      // Only steps without effect left (someone else removed what they changed).
      set({ [from]: [] } as Partial<EditorState>);
    };

    return {
      doc,
      pageIndex: 0,
      selection: [],
      hoverId: null,
      editingTextId: null,
      croppingId: null,
      zoom: 0.5,
      pan: { x: 0, y: 0 },
      readOnly,
      guides: [],
      past: [],
      future: [],
      lastMergeKey: null,
      lastMergeAt: 0,
      gestureBase: null,
      gestureEntry: null,
      gestureLive: false,
      changeKind: "edit",
      brand: null,
      fitRequest: null,

      update(recipe, options) {
        const state = get();
        if (state.readOnly) return;
        // Values changed by hand lose their brand link ("detach on edit").
        const next = normalized(
          pruneBindings(state.doc, produce(state.doc, recipe)),
          !state.gestureBase,
        );
        if (next === state.doc) return;
        const redo = diff(state.doc, next);
        const undo = diff(next, state.doc);
        if (state.gestureBase) {
          set({
            doc: next,
            gestureEntry: appendChange(state.gestureEntry ?? EMPTY_ENTRY, redo, undo),
            changeKind: "edit",
          });
          return;
        }
        const now = Date.now();
        const last = state.past[state.past.length - 1];
        const merge =
          last !== undefined &&
          options?.merge !== undefined &&
          options.merge === state.lastMergeKey &&
          now - state.lastMergeAt < MERGE_WINDOW_MS;
        set({
          doc: next,
          past: merge
            ? [...state.past.slice(0, -1), appendChange(last, redo, undo)]
            : [...state.past, { redo, undo }].slice(-HISTORY_LIMIT),
          future: [],
          lastMergeKey: options?.merge ?? null,
          lastMergeAt: now,
          selection: withoutMissing(next, state.selection),
          changeKind: "edit",
        });
      },

      patch(recipe) {
        const state = get();
        if (state.readOnly) return;
        const next = produce(state.doc, recipe);
        if (next !== state.doc) set({ doc: next, changeKind: "local" });
      },

      beginGesture(options) {
        const state = get();
        if (state.readOnly || state.gestureBase) return;
        set({ gestureBase: state.doc, gestureEntry: null, gestureLive: options?.live ?? false });
      },

      endGesture() {
        const state = get();
        if (!state.gestureBase) return;
        let entry = state.gestureEntry;
        // Tracks emptied by the drag go now.
        const doc = entry ? normalized(state.doc, true) : state.doc;
        if (entry && doc !== state.doc) {
          entry = appendChange(entry, diff(state.doc, doc), diff(doc, state.doc));
        }
        set({
          doc,
          ...(doc !== state.doc ? { changeKind: "edit" as const } : {}),
          gestureBase: null,
          gestureEntry: null,
          gestureLive: false,
          guides: [],
          ...(entry && entry.redo.length > 0
            ? {
                past: [...state.past, entry].slice(-HISTORY_LIMIT),
                future: [],
                lastMergeKey: null,
              }
            : {}),
        });
      },

      cancelGesture() {
        const state = get();
        if (!state.gestureBase) return;
        const doc = state.gestureEntry
          ? applyHistory(state.doc, state.gestureEntry.undo)
          : state.doc;
        set({
          doc,
          gestureBase: null,
          gestureEntry: null,
          gestureLive: false,
          guides: [],
          changeKind: "edit",
        });
      },

      undo() {
        travel("past");
      },

      redo() {
        travel("future");
      },

      replaceDocument(replacement, options) {
        const state = get();
        const next = state.brand ? applyBrand(replacement, state.brand) : replacement;
        set({
          doc: next,
          past: options?.keepHistory
            ? [...state.past, { redo: diff(state.doc, next), undo: diff(next, state.doc) }].slice(
                -HISTORY_LIMIT,
              )
            : [],
          future: [],
          selection: withoutMissing(next, state.selection),
          pageIndex: Math.min(state.pageIndex, next.pages.length - 1),
          editingTextId: null,
          croppingId: null,
          gestureBase: null,
          gestureEntry: null,
          gestureLive: false,
          changeKind: "edit",
        });
      },

      applyRemote(next) {
        const state = get();
        if (next === state.doc) return;
        // Stay on the same page, even when others add or move pages.
        const pageId = state.doc.pages[state.pageIndex]?.id;
        const moved = next.pages.findIndex((page) => page.id === pageId);
        const pageIndex = moved >= 0 ? moved : Math.min(state.pageIndex, next.pages.length - 1);
        set({
          doc: next,
          pageIndex,
          selection:
            moved >= 0 || pageId === undefined ? withoutMissing(next, state.selection) : [],
          editingTextId:
            state.editingTextId && next.elements[state.editingTextId] ? state.editingTextId : null,
          croppingId: state.croppingId && next.elements[state.croppingId] ? state.croppingId : null,
          hoverId: state.hoverId && next.elements[state.hoverId] ? state.hoverId : null,
          changeKind: "remote",
        });
      },

      select(ids, mode = "replace") {
        const state = get();
        const valid = withoutMissing(state.doc, ids);
        let selection: string[];
        if (mode === "replace") selection = valid;
        else if (mode === "add") selection = [...new Set([...state.selection, ...valid])];
        else {
          const set = new Set(state.selection);
          for (const id of valid) {
            if (set.has(id)) set.delete(id);
            else set.add(id);
          }
          selection = [...set];
        }
        set({
          selection,
          editingTextId:
            state.editingTextId && selection.includes(state.editingTextId)
              ? state.editingTextId
              : null,
          croppingId:
            state.croppingId && selection.includes(state.croppingId) ? state.croppingId : null,
        });
      },

      setHover(id) {
        if (get().hoverId !== id) set({ hoverId: id });
      },

      setPage(index) {
        const state = get();
        const pageIndex = Math.max(0, Math.min(index, state.doc.pages.length - 1));
        if (pageIndex !== state.pageIndex) {
          set({ pageIndex, selection: [], editingTextId: null, croppingId: null });
        }
      },

      goToPage(index) {
        get().setPage(index);
        if (isBoardView(get().doc)) get().requestFit("focus");
      },

      setView(view) {
        set((state) => ({
          zoom: view.zoom !== undefined ? Math.max(0.02, Math.min(32, view.zoom)) : state.zoom,
          pan: view.pan ?? state.pan,
        }));
      },

      setEditingText(id) {
        if (get().readOnly && id) return;
        set({ editingTextId: id, croppingId: null });
      },

      setCropping(id) {
        if (get().readOnly && id) return;
        set({ croppingId: id, editingTextId: null });
      },

      setGuides(guides) {
        const current = get().guides;
        if (current.length === 0 && guides.length === 0) return;
        set({ guides });
      },

      setReadOnly(readOnly) {
        set({ readOnly, editingTextId: null, croppingId: null });
      },

      requestFit(mode) {
        set({ fitRequest: { mode, id: (get().fitRequest?.id ?? 0) + 1 } });
      },

      setBrand(tokens) {
        const state = get();
        if (!tokens) {
          if (state.brand) set({ brand: null });
          return;
        }
        const doc = applyBrand(state.doc, tokens);
        set({
          brand: tokens,
          doc,
          gestureBase: state.gestureBase ? applyBrand(state.gestureBase, tokens) : null,
          // Refreshed brand values alone are not an edit: opening a file never
          // changes it for the others. Exports resolve the brand themselves.
          changeKind: doc === state.doc ? state.changeKind : "local",
        });
      },
    };
  });
}

export const EditorContext = createContext<EditorStore | null>(null);

export function useEditorApi(): EditorStore {
  const store = useContext(EditorContext);
  if (!store) throw new Error("Editor-Store fehlt");
  return store;
}

export function useEditor<T>(selector: (state: EditorState & EditorActions) => T): T {
  return useStore(useEditorApi(), selector);
}

/**
 * The document as seen by the active page: `format` carries the size of that page
 * (identical to the document unless the page has its own size).
 */
export function activePageDoc(store: EditorStore): SceneDocument {
  const { doc, pageIndex } = store.getState();
  return pageDocument(doc, pageIndex);
}

/** The editor shows all pages side by side (designs with the board switched on). */
export function isBoardView(doc: SceneDocument): boolean {
  return doc.board === true && supportsBoard(doc);
}

/** Top-left corner of the active page on the board (0/0 without a board). */
export function pageOrigin(doc: SceneDocument, pageIndex: number): { x: number; y: number } {
  if (!isBoardView(doc)) return { x: 0, y: 0 };
  const rect = boardRects(doc)[pageIndex];
  return rect ? { x: rect.x, y: rect.y } : { x: 0, y: 0 };
}

/**
 * Screen position of the active page's top-left corner. Everything that edits the
 * active page (pointer to page coordinates, rulers, drawing) works from it; `pan`
 * itself is the position of the board.
 */
export function pagePan(state: Pick<EditorState, "doc" | "pageIndex" | "pan" | "zoom">): {
  x: number;
  y: number;
} {
  const origin = pageOrigin(state.doc, state.pageIndex);
  return { x: state.pan.x + origin.x * state.zoom, y: state.pan.y + origin.y * state.zoom };
}

/** `pagePan` for components (a new object only when the position changes). */
export function usePagePan(): { x: number; y: number } {
  const panX = useEditor((s) => s.pan.x);
  const panY = useEditor((s) => s.pan.y);
  const zoom = useEditor((s) => s.zoom);
  const originX = useEditor((s) => pageOrigin(s.doc, s.pageIndex).x);
  const originY = useEditor((s) => pageOrigin(s.doc, s.pageIndex).y);
  return { x: panX + originX * zoom, y: panY + originY * zoom };
}
